import { Directive, HostListener, ElementRef, EventEmitter, Output, OnInit, OnDestroy, Input } from '@angular/core';
import { NgControl } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs';

@Directive({
  selector: '[ssnMask]',
  standalone: true
})
export class SsnMaskDirective implements OnInit, OnDestroy {
  @Input() maskType: 'SSN' | 'ITIN' = 'SSN';
  @Output() maskBlur = new EventEmitter<{ type: string; value: string }>();

  private observer!: MutationObserver;
  private readonly destroy$ = new Subject<void>();

  constructor(private control: NgControl, private el: ElementRef<HTMLInputElement>) {}

  ngOnInit(): void {
    setTimeout(() => this.updateDisplay(), 0);
    this.observer = new MutationObserver(() => this.updateDisplay());
    this.observer.observe(this.el.nativeElement, { attributes: true, attributeFilter: ['data-show-ssn'] });

    // When the control is patched (e.g. async load), refresh masked display — MutationObserver only watches data-show-ssn.
    const ctrl = this.control.control;
    if (ctrl) {
      ctrl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
        if (this.el.nativeElement !== document.activeElement) {
          // Defer so DefaultValueAccessor / parent updates finish first
          setTimeout(() => this.updateDisplay(), 0);
        }
      });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.observer) this.observer.disconnect();
  }

  private formatSSN(value: string): string {
    const digits = value.replace(/\D/g, '').substring(0, 9);
    if (digits.length >= 6) return `${digits.slice(0,3)}-${digits.slice(3,5)}-${digits.slice(5)}`;
    if (digits.length >= 4) return `${digits.slice(0,3)}-${digits.slice(3)}`;
    return digits;
  }

  private maskSSN(value: string): string {
    const digits = value.replace(/\D/g, '');
    if (digits.length === 9) return `***-**-${digits.slice(5)}`;
    return value.replace(/\d/g, '*');
  }

  private updateDisplay(): void {
    const showSSN = this.el.nativeElement.getAttribute('data-show-ssn') === 'true';
    const value = this.control.control?.value || '';
    const displayValue = showSSN ? this.formatSSN(value) : this.maskSSN(value);
    (this.control.valueAccessor as any).writeValue(displayValue);
    
    // Set aria-label for screen readers to properly announce hyphens
    if (!showSSN && displayValue.includes('-')) {
      const ariaValue = displayValue.replace(/-/g, ' hyphen ');
      this.el.nativeElement.setAttribute('aria-label', `${this.maskType}: ${ariaValue}`);
    } else {
      this.el.nativeElement.removeAttribute('aria-label');
    }
  }

  @HostListener('input', ['$event'])
  onInput(event: any): void {
    const raw = String(event.target?.value ?? '');
    // Masked DOM (e.g. ***-**-1234) must not sync into the model — digits-only parse would truncate to the visible tail.
    if (raw.includes('*')) {
      return;
    }
    const formatted = this.formatSSN(raw);
    this.control.control?.setValue(formatted, { emitEvent: false });
    const showSSN = this.el.nativeElement.getAttribute('data-show-ssn') === 'true';
    const isFocused = this.el.nativeElement === document.activeElement;
    // While focused, always show formatted so the user can edit; when blurred + hidden, mask (covers patchValue / synthetic input).
    if (showSSN || isFocused) {
      event.target.value = formatted;
    } else {
      event.target.value = this.maskSSN(formatted);
    }
  }

  @HostListener('focus')
  onFocus(): void {
    const value = this.control.control?.value || '';
    const formatted = this.formatSSN(value);
    this.control.control?.setValue(formatted, { emitEvent: false });
    (this.control.valueAccessor as any).writeValue(formatted);
  }

  @HostListener('blur')
  onBlur(): void {
    this.updateDisplay();
    const value = this.control.control?.value || '';
    this.maskBlur.emit({ type: this.maskType, value });
  }
}